import React from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import { PreferencesProvider, usePreferences } from './hooks/usePreferences';
import { PageLoader } from './components/ui/PageLoader';

const ProtectedRoutesGate: React.FC = () => {
  const { isLoaded, isSignedIn } = useAuth();
  const { preferences, loading: preferencesLoading } = usePreferences();
  const location = useLocation();

  // Show branded loader while Clerk is loading
  if (!isLoaded) {
    return <PageLoader label="Signing you in" />;
  }

  // If not signed in, redirect to home page
  if (!isSignedIn) {
    return <Navigate to="/" replace />;
  }

  // Wait for preferences to load before deciding on the onboarding gate
  if (preferencesLoading) {
    return <PageLoader label="Loading your workspace" />;
  }

  const isOnboarded = !!preferences?.onboardingCompletedAt;
  const isOnboardingRoute = location.pathname.startsWith('/onboarding');

  // Not onboarded yet: force the user into the onboarding wizard, unless
  // preferences failed to load (avoid trapping the user behind a broken gate).
  if (preferences && !isOnboarded && !isOnboardingRoute) {
    return <Navigate to="/onboarding" replace />;
  }

  // Already onboarded: never show the onboarding wizard again.
  if (preferences && isOnboarded && isOnboardingRoute) {
    return <Navigate to="/dashboard" replace />;
  }

  // If signed in, render nested routes
  return <Outlet />;
};

const ProtectedRoutes: React.FC = () => (
  <PreferencesProvider>
    <ProtectedRoutesGate />
  </PreferencesProvider>
);

export default ProtectedRoutes;
